// src/utils/tokens/chartConfig.ts

import type { EChartsOption } from 'echarts';
import type { MetricInfo } from '@/types/metrics';
import { MetricKeys } from '@/constants/metrics';

export type ChartType = 'line' | 'bar' | 'stacked' | 'percent';

/**
 * Helper function for compact number formatting (B/M/K)
 */
export function formatCompact(value: number): string {
  const absValue = Math.abs(value);
  if (absValue >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (absValue >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (absValue >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
  return value.toFixed(2);
}

/**
 * Helper to format value with unit (handles USD specially)
 */
export function formatWithUnit(
  value: number,
  unit: string | null | undefined,
  compact: boolean = false
): string {
  const formatted = compact ? formatCompact(value) : value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (!unit) return formatted;
  // Handle USD specially - prefix with $
  if (unit.toLowerCase() === 'usd' || unit === '$') {
    return `$${formatted}`;
  }
  // Other units - suffix
  return `${formatted} ${unit}`;
}

/**
 * Determine if a metric can be indexed to 100
 */
export function canIndexMetric(
  metricKey: string,
  isComputedMetric: boolean,
  metricInfo: MetricInfo | undefined
): boolean {
  return !isComputedMetric && 
    !metricKey.startsWith('ratio:') && 
    metricInfo?.metric_class !== 'flow' &&
    (metricKey === MetricKeys.PriceClose || 
     metricKey === MetricKeys.MarketCap ||
     metricKey === 'fdv' ||
     metricKey === 'tvl');
}

/**
 * Get Y-axis configuration based on metric type
 */
export function getYAxisConfig(
  metricKey: string,
  chartType: ChartType,
  isComputedMetric: boolean,
  metricInfo: MetricInfo | undefined,
  shouldNormalize: boolean,
  labelFromCatalog: (key: string) => string
): {
  yAxisName: string;
  yAxisFormatter?: (value: number) => string;
  tooltipFormatter?: (value: number) => string;
} {
  const isPercent = chartType === 'percent';
  const isFundingRate = metricKey.includes('fr_oiw') || metricKey.includes('fr_volw') || metricKey.includes('funding');
  const isFlowMetric = metricInfo?.metric_class === 'flow';
  const unit = metricInfo?.unit;
  
  if (isPercent) {
    return {
      yAxisName: 'Percentage',
      yAxisFormatter: (value: number) => `${value}%`,
      tooltipFormatter: (value: number) => `${value.toFixed(2)}%`,
    };
  }
  
  if (shouldNormalize) {
    return {
      yAxisName: 'Index (start=100)',
      tooltipFormatter: (value: number) => value.toFixed(2),
    };
  }
  
  if (isFundingRate) {
    return {
      yAxisName: 'Funding Rate (%)',
      yAxisFormatter: (value: number) => `${value.toFixed(4)}%`,
      tooltipFormatter: (value: number) => `${value.toFixed(6)}%`,
    };
  }
  
  if (isComputedMetric) {
    if (metricKey.includes('vol') || metricKey.includes('drawdown')) {
      return {
        yAxisName: 'Percentage',
        yAxisFormatter: (value: number) => `${value.toFixed(1)}%`,
        tooltipFormatter: (value: number) => `${value.toFixed(2)}%`,
      };
    }
    if (metricKey.includes('sharpe') || metricKey.includes('sortino')) {
      return {
        yAxisName: 'Ratio',
        tooltipFormatter: (value: number) => value.toFixed(3),
      };
    }
  }
  
  if (metricKey.startsWith('ratio:')) {
    if (unit) {
      return {
        yAxisName: labelFromCatalog(metricKey),
        yAxisFormatter: (value: number) => formatWithUnit(value, unit, true),
        tooltipFormatter: (value: number) => formatWithUnit(value, unit, false),
      };
    }
    return {
      yAxisName: 'Ratio',
      tooltipFormatter: (value: number) => value.toFixed(3),
    };
  }
  
  if (isFlowMetric) {
    return {
      yAxisName: labelFromCatalog(metricKey),
      yAxisFormatter: (value: number) => formatWithUnit(value, unit, true),
      tooltipFormatter: (value: number) => formatWithUnit(value, unit, true),
    };
  }
  
  // Stock metrics (when not indexed)
  return {
    yAxisName: labelFromCatalog(metricKey),
    yAxisFormatter: (value: number) => formatWithUnit(value, unit, true),
    tooltipFormatter: (value: number) => formatWithUnit(value, unit, false),
  };
}

/**
 * Build ECharts option for comparison chart
 */
export function buildChartOption(
  processedData: Array<{ ticker: string; dates: number[]; values: number[] }>,
  chartType: ChartType,
  logScale: boolean,
  yAxisConfig: {
    yAxisName: string;
    yAxisFormatter?: (value: number) => string;
    tooltipFormatter?: (value: number) => string;
  }
): EChartsOption {
  const isPercent = chartType === 'percent';
  const isStacked = chartType === 'stacked' || isPercent;
  
  const series: Array<Record<string, unknown>> = [];
  for (const item of processedData) {
    const pts: Array<[number, number]> = item.dates.map((date, i) => [date, item.values[i] || 0]);
    const seriesConfig: Record<string, unknown> = {
      name: item.ticker,
      type: chartType === 'bar' ? 'bar' : 'line',
      data: pts,
      smooth: false,
      showSymbol: false,
      lineStyle: chartType === 'line' || !isStacked ? { width: 2 } : undefined,
      emphasis: { focus: 'series' },
    };
    if (isStacked) {
      seriesConfig['stack'] = 'total';
      seriesConfig['areaStyle'] = { opacity: 0.7 };
    } else if (chartType === 'line') {
      seriesConfig['areaStyle'] = { opacity: 0.15 };
    }
    series.push(seriesConfig);
  }

  const yAxisOpts: Record<string, unknown> = {
    name: yAxisConfig.yAxisName,
    nameTextStyle: { color: '#94a3b8', fontSize: 11 },
    axisLabel: { 
      color: '#94a3b8',
      formatter: yAxisConfig.yAxisFormatter,
    },
    splitLine: { lineStyle: { color: 'rgba(148,163,184,0.15)' } },
  };

  if (isPercent) {
    yAxisOpts['type'] = 'value';
    yAxisOpts['min'] = 0;
    yAxisOpts['max'] = 100;
  } else if (logScale) {
    let minPositive = Infinity;
    for (const item of processedData) {
      for (const val of item.values) {
        if (val > 0 && val < minPositive) {
          minPositive = val;
        }
      }
    }
    if (Number.isFinite(minPositive) && minPositive > 0) {
      yAxisOpts['type'] = 'log';
      yAxisOpts['logBase'] = 10;
      yAxisOpts['min'] = Math.max(0.1, Math.pow(10, Math.floor(Math.log10(minPositive))));
    } else {
      yAxisOpts['type'] = 'value';
    }
  } else {
    yAxisOpts['type'] = 'value';
  }

  return {
    backgroundColor: 'transparent',
    tooltip: { 
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      valueFormatter: yAxisConfig.tooltipFormatter ? (v) => (v == null ? '-' : yAxisConfig.tooltipFormatter!(Number(v))) : undefined,
      order: 'valueDesc',
    },
    legend: { 
      top: 0, 
      textStyle: { color: '#cbd5e1' },
      icon: 'rect',
      itemWidth: 14,
      itemHeight: 3,
    },
    grid: { left: 70, right: 16, top: 40, bottom: 80 },
    xAxis: { 
      type: 'time', 
      axisLabel: { color: '#94a3b8' }, 
      axisLine: { lineStyle: { color: 'rgba(148,163,184,0.3)' } },
      boundaryGap: chartType === 'bar',
    },
    yAxis: yAxisOpts,
    dataZoom: [
      {
        type: 'slider',
        start: 0,
        end: 100,
        height: 32,
        bottom: 5,
        borderColor: 'rgba(71,85,105,0.4)',
        borderWidth: 1,
        backgroundColor: 'rgba(15,23,42,0.6)',
        fillerColor: 'rgba(59,130,246,0.15)',
        handleIcon: 'path://M0,0 L0,32 L6,32 L6,0 Z',
        handleSize: '80%',
        handleStyle: { 
          color: 'rgba(71,85,105,0.8)',
          borderColor: 'rgba(148,163,184,0.6)',
          borderWidth: 1,
        },
        moveHandleSize: 5,
        moveHandleStyle: { 
          color: 'rgba(71,85,105,0.8)',
          opacity: 0.8,
        },
        emphasis: {
          handleStyle: {
            color: 'rgba(100,116,139,0.9)',
            borderColor: 'rgba(148,163,184,0.8)',
          },
          moveHandleStyle: {
            color: 'rgba(100,116,139,0.9)',
          },
        },
        textStyle: { color: '#64748b', fontSize: 11 },
        dataBackground: {
          lineStyle: { 
            color: 'rgba(148,163,184,0.4)',
            width: 1,
          },
          areaStyle: { 
            color: 'rgba(71,85,105,0.3)',
          },
        },
        selectedDataBackground: {
          lineStyle: {
            color: 'rgba(59,130,246,0.5)',
            width: 1,
          },
          areaStyle: {
            color: 'rgba(59,130,246,0.2)',
          },
        },
      },
      {
        type: 'inside',
        start: 0,
        end: 100,
      },
    ],
    series,
  } as EChartsOption;
}

